import React, { useState } from 'react';
import { Share2, Check } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

/**
 * Builds a shareable URL pointing to the backend SSR share endpoint.
 * Scrapers (WhatsApp, FB, Twitter) hit this URL and read the correct og:tags,
 * humans get a clean preview card with a CTA back to the React SPA.
 *
 * Props:
 *  - slug: dealer slug (required)
 *  - listingId: optional listing slug or id for car-detail share
 *  - title / text: passed to the native Web Share sheet on mobile
 *  - variant: 'light' | 'dark' | 'pill' | 'icon'
 */
const ShareLinkButton = ({
  slug,
  listingId,
  title,
  text,
  variant = 'light',
  label = 'Share',
  className = '',
  testId = 'share-link-button',
}) => {
  const [copied, setCopied] = useState(false);
  if (!slug) return null;

  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const shareUrl = listingId
    ? `${base}/api/share/dealers/${slug}/cars/${listingId}`
    : `${base}/api/share/dealers/${slug}`;

  const handleShare = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: title || 'Propitstop Drive',
          text: text || 'Check this out on Propitstop Drive',
          url: shareUrl,
        });
        return;
      } catch (err) {
        // User cancelled — fall through to clipboard
        if (err?.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({ title: 'Share link copied!', description: 'Paste it on WhatsApp, Instagram, or anywhere.' });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({ title: 'Copy failed', description: shareUrl, variant: 'destructive' });
    }
  };

  const styles = {
    light:
      'inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 bg-white text-xs uppercase tracking-[0.18em] font-bold text-zinc-700 hover:bg-zinc-50 transition-colors',
    dark:
      'inline-flex items-center gap-3 px-5 sm:px-7 py-3.5 sm:py-4 bg-white/10 hover:bg-white/15 backdrop-blur text-white font-bold ring-1 ring-white/30 transition-colors',
    pill:
      'group w-full inline-flex items-center justify-between px-5 py-4 bg-white hover:bg-zinc-50 border border-zinc-300 text-zinc-900 font-bold text-sm uppercase tracking-[0.18em] transition-colors',
    icon:
      'inline-flex items-center justify-center w-10 h-10 border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 transition-colors',
  };

  const Icon = copied ? Check : Share2;

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={handleShare}
        data-testid={testId}
        title={copied ? 'Copied!' : 'Copy share link'}
        className={`${styles.icon} ${className}`}
      >
        <Icon className="w-4 h-4" />
      </button>
    );
  }

  if (variant === 'pill') {
    return (
      <button
        type="button"
        onClick={handleShare}
        data-testid={testId}
        className={`${styles.pill} ${className}`}
      >
        <span className="inline-flex items-center gap-2">
          <Icon className="w-4 h-4" /> {copied ? 'Link copied' : label}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleShare}
      data-testid={testId}
      className={`${styles[variant] || styles.light} ${className}`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="text-xs sm:text-sm uppercase tracking-[0.18em] sm:tracking-[0.2em]">
        {copied ? 'Copied!' : label}
      </span>
    </button>
  );
};

export default ShareLinkButton;
